<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import {
		update,
		edit,
		initialEdit,
		resetEditUpdateStore,
		bulkActionEdit,
	} from 'src/stores/ui/edit_lists';
	import type { UpdateModeSubTypes, UpdateModeTypes } from 'src/stores/ui/edit_lists';
	//Stores
	import { resetFiltersAllDevicesList } from 'src/stores/ui/filters';
	import { user_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';

	//Design ststem components
	import {
		Button,
		Checkbox,
		DropdownMenu,
		MenuItemList,
		IconButton,
	} from '@Shelly/shelly-design-system';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { resetDrawer } from 'src/helpers/drawer_helpers';

	import { isSubAccount } from 'src/helpers/user_helpers';
	import { buildDropdownMenus } from './Scenes/helpers/menuBuilders';
	import { getButtonText } from 'src/helpers/device_data_helpers';
	import ChooseAIModalModal from './ChooseAIModalModal.svelte';
	import BatchCommandModal from './BatchCommandModals/BatchCommandModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { firstCharCapOnly } from 'src/helpers/utilities';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';

	const dispatchSelectAllClick = createEventDispatcher<{ 'select-all': {} }>();
	const dispatchSubmit = createEventDispatcher<{ 'submit-click': {} }>();

	export let isMobile: boolean = false;
	export let areFiltersDisabled: boolean = false;
	export let disableBatchCommandButton: boolean = false;
	export let useSecondaryEllipsisVariant: boolean = false;

	let windowClientWidth: number;
	let showDropdownMenu = false;
	let showModalMenu = false;

	let dropdownMenuInitialEditCommands = [];
	let dropdownMenuBulkCommands = [];
	let dropdownMenuMenuData = [];

	let showAIModelCommandsModal = false;
	let showSharedManagedModal = false;

	let customButtonText: string = '';

	const AIModelCommands = [
		{
			name: 'text_enable_ai_model',
			id: 'enable_ai_model' as UpdateModeSubTypes,
			icon: 'fak fa-solid-sparkles icon-ai-color',
		},
		{
			name: 'text_disable_ai_model',
			id: 'disable_ai_model' as UpdateModeSubTypes,
			icon: 'fak fa-solid-sparkles',
		},
		{
			name: 'manage_ai_notifications',
			id: 'manage_ai_notifications' as UpdateModeSubTypes,
			icon: 'fa-light fa-bell',
		},
	];

	$: {
		const { initialEditCommands, bulkCommands } = buildDropdownMenus({
			format: $format,
			userData: $user_data,
			setShowDropdownMenu: (v) => (showDropdownMenu = v),
			setShowAIModelCommandsModal: (v) => (showAIModelCommandsModal = v),
			dispatchSubmit: () => dispatchSubmit('submit-click'),
			onChangeClick: onChangeClick,
			updateStore: updateStore,
			newLayout: $showNewLayout,
		});

		dropdownMenuInitialEditCommands = initialEditCommands;
		dropdownMenuBulkCommands = bulkCommands;
	}

	$: dropdownMenuMenuData = $initialEdit ? dropdownMenuInitialEditCommands : dropdownMenuBulkCommands;
	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
	$: isTablet = windowClientWidth <= SystemScreenBreakpoints.TABLET_MAX;
	$: isEmptyDeviceList = Object.values($devices).length === 0;
	$: selectedDevicesLength = Object.keys($update.selectedDevices).length;

	const onToggleDropdownMenu = () => {
		if (!isMobile && !isTablet) {
			showDropdownMenu = !showDropdownMenu;
		} else {
			showModalMenu = !showModalMenu;
		}
	};

	const closeModalMenu = () => {
		showModalMenu = false;
	};

	$: buttonText = customButtonText || getButtonText($update.type, $format);

	// Helper to get AI command display text
	const getAICommandText = (subType: UpdateModeSubTypes): string => {
		const command = AIModelCommands.find((cmd) => cmd.id === subType);
		return command ? $format(command.name) : '';
	};

	const updateStore = (
		type: UpdateModeTypes,
		subType: UpdateModeSubTypes = '' as UpdateModeSubTypes,
	) => {
		bulkActionEdit.set(true);
		areFiltersDisabled = true;
		update.update((s) => {
			return {
				...s,
				value: true,
				type: type,
				subType: subType,
			};
		});
		edit.set(true);
	};

	const onChangeClick = (type: UpdateModeTypes, subType = '' as UpdateModeSubTypes) => {
		updateStore(type, subType);
		resetFiltersAllDevicesList();

		if (type === 'ai_device_failure' && subType) {
			customButtonText = getAICommandText(subType);
		} else {
			customButtonText = '';
		}
	};

	const clearSubmenuStates = () => {
		dropdownMenuMenuData.forEach((item) => {
			if (item?.hasOwnProperty('showDropdown')) {
				item.showDropdown = false;
			}
		});

		dropdownMenuMenuData = dropdownMenuMenuData;
	};
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

{#if isActiveHomeShared($homes.list)}
	<!-- Shared homes: keep the affordance but explain that bulk actions are owner-managed -->
	<IconButton
		aria-label={$format('title_more', { default: 'More' })}
		variant={useSecondaryEllipsisVariant ? 'secondary' : 'primary'}
		class="align-self-center"
		icon="fa-regular fa-ellipsis-vertical"
		on:click={() => (showSharedManagedModal = true)}
	/>
	<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="generic" />
{:else if !isSubAccount()}
	{#if !$edit || !$update.value}
		<DropdownMenu bind:show={showDropdownMenu} class="align-self-center">
			<IconButton
				aria-label={$format('title_more', { default: 'More' })}
				variant={useSecondaryEllipsisVariant ? 'secondary' : $initialEdit ? 'danger' : 'primary'}
				isActive={showDropdownMenu}
				disabled={disableBatchCommandButton}
				slot="trigger"
				icon="fa-regular fa-ellipsis-vertical"
				on:click={onToggleDropdownMenu}
			/>
			{#each dropdownMenuMenuData as dropdownSubmenuItem, i}
				{#if dropdownSubmenuItem.hasOwnProperty('showDropdown')}
					<DropdownMenu
						class="width-full"
						bind:show={dropdownSubmenuItem.showDropdown}
						positionHorizontally
						on:close={() => {
							dropdownMenuMenuData[i].active = false;
						}}
					>
						<MenuItemList
							hasBorder
							title={dropdownSubmenuItem.title ===
							$format('sub_settings_title_ai_model', { default: 'AI Model' })
								? dropdownSubmenuItem.title
								: firstCharCapOnly(dropdownSubmenuItem.title)}
							icon={dropdownSubmenuItem.icon}
							rightIcon={dropdownSubmenuItem.rightIcon}
							active={dropdownSubmenuItem.active}
							slot="trigger"
							on:mouseenter={() => {
								dropdownMenuMenuData[i].showDropdown = true;
								dropdownMenuMenuData[i].active = true;
							}}
							on:click={() => {
								if (windowClientWidth <= SystemScreenBreakpoints.MOBILE_MAX) {
									dropdownSubmenuItem.showDropdown = !dropdownSubmenuItem.showDropdown;
								}
							}}
						/>
						{#each dropdownSubmenuItem.items as submenuItem}
							<MenuItemList
								title={submenuItem.title}
								icon={submenuItem.icon}
								on:click={() => {
									if (
										submenuItem.id === 9 ||
										submenuItem.id === 10 ||
										submenuItem.id === 11
									) {
										onChangeClick('ai_device_failure', submenuItem.aiSubtype);
									}
									clearSubmenuStates();
									dropdownMenuMenuData[i].showDropdown = false;
									dropdownMenuMenuData[i].active = false;
								}}
							/>
						{/each}
					</DropdownMenu>
				{:else}
					<MenuItemList
						title={firstCharCapOnly(dropdownSubmenuItem.title)}
						icon={dropdownSubmenuItem.icon}
						on:mouseenter={() => {
							clearSubmenuStates();
						}}
						on:click={() => {
							resetDrawer();
							dropdownSubmenuItem.callback();
							initialEdit.set(false);
						}}
						disabled={isEmptyDeviceList}
					/>
				{/if}
			{/each}
		</DropdownMenu>
	{:else}
		<div
			class={classMap({
				'd-flex align-items-center': true,
				'flex-wrap': isMobile,
			})}
		>
			<div class="d-flex width-full">
				<Checkbox
					size="m"
					id="checkbox-select-all"
					class="flex-shrink-0 mr-s text-capitalize"
					checked={$update.selectAll}
					disabled={$update.subType === 'enable_ai_model'}
					on:click={(e) => {
						update.update((s) => {
							return {
								...s,
								selectAll: e.target.checked,
							};
						});
						edit.set(true);
						dispatchSelectAllClick('select-all', e);
					}}
				>
					{$format('lebel_select_all', { default: 'select all' })}
				</Checkbox>
				<Button
					variant="primary"
					size="m"
					class="width-md-auto width-sm-full mr-sm-s mr-md-s text-capitalize"
					on:click={() => {
						dispatchSubmit('submit-click');
					}}
					{...selectedDevicesLength > 0
						? {
								notification: {
									type: 'danger',
									title: selectedDevicesLength,
								},
							}
						: {}}
					disabled={selectedDevicesLength === 0}
				>
					{buttonText}
				</Button>
				<IconButton
					aria-label={$format('cancel', { default: 'Cancel' })}
					variant="secondary"
					icon="fas fa-times"
					on:click={() => {
						areFiltersDisabled = false;
						resetEditUpdateStore();
						resetFiltersAllDevicesList();
						customButtonText = '';
					}}
				/>
			</div>
		</div>
	{/if}

	<!-- MODAL Choose an AI Model Command  -->
	<ChooseAIModalModal
		aiModelCommands={AIModelCommands}
		bind:showAIModelCommandsModal
		{onChangeClick}
	/>

	<BatchCommandModal
		bind:showModalMenu
		{dropdownMenuMenuData}
		{isEmptyDeviceList}
		{AIModelCommands}
		on:close={closeModalMenu}
		on:menu-item-click={(e) => {
			resetDrawer();
			e.detail.callback();
			initialEdit.set(false);
		}}
		on:ai-command-click={(e) => {
			onChangeClick(e.detail.type, e.detail.subType);
		}}
	/>
{/if}
<!--END OF MODAL Choose an AI Model Command  -->
